import React, { useState, useEffect, useCallback } from "react";
import InnerPageLayout from "../components/Layout/InnerPageLayout";
import BreadCrumb from "../components/Breadcrumb/BreadCrumb";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// Import components
import BlogCard from "../components/Blog/BlogCard";
import Pagination from "../components/Blog/Pagination";
import BlogSidebar from "../components/BlogInfo/BlogSidebar";

// Import API service
import blogService from "../services/blogService";
import { useContentAvailability } from "../utils/contentAvailability";


export default function Blogs() {
  const { hasBlogs } = useContentAvailability();
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || "");
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  const postsPerPage = parseInt(process.env.REACT_APP_ITEMS_PER_PAGE) || 10;

  // Fetch blogs from API
  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: postsPerPage,
        ...(selectedCategory && { category: selectedCategory }),
        ...(searchTerm && { search: searchTerm })
      };

      const response = await blogService.getAllBlogs(params);

      if (response.success) {
        // Transform API data to match frontend format
        const transformedBlogs = response.data.map(blog => ({
          ...blog,
          tags: blog.tags || [],
          content: {
            intro: blog.content_intro,
            sections: blog.content_sections || []
          }
        }));

        setBlogs(transformedBlogs);
        setPagination(response.pagination);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Unable to load blogs. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategory, searchTerm, postsPerPage]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCategory) params.set('category', selectedCategory);
    if (searchTerm) params.set('search', searchTerm);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params, { replace: true });
  }, [selectedCategory, searchTerm, currentPage, setSearchParams]);

  // Event handlers
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Return null if blogs are not available
  if (!hasBlogs) {
    return null;
  }

  return (
    <InnerPageLayout>
      <Helmet>
        <title>Blogs - Impera | Insights & News</title>
        <meta name="description" content="Explore the latest insights, news, and trends in technology and business on the Impera blog." />
      </Helmet>
      <BreadCrumb pageTitle="Blogs" items={[{ label: "Blogs", path: "/blogs" }]} />
      <section className="py-16 md:py-[130px]">
        <div className="theme-container w-full mx-auto grid grid-cols-8 lg:grid-cols-12 gap-y-16 lg:gap-y-0 lg:gap-x-[70px]">
          {/* Blogs Section */}
          <div className="col-span-8">
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Search Results Info */}
            {!loading && (searchTerm || selectedCategory) && (
              <div className="mb-6 p-4 bg-purple/10 rounded-lg">
                <p className="text-white">
                  Showing {pagination.total} result{pagination.total !== 1 ? 's' : ''}
                  {searchTerm && ` for "${searchTerm}"`}
                  {selectedCategory && ` in "${selectedCategory}"`}
                </p>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="text-center py-16">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple"></div>
                <p className="text-white mt-4">Loading blogs...</p>
              </div>
            ) : blogs.length > 0 ? (
              <>
                {/* Blog Cards */}
                {blogs.map((blog, index) => (
                  <BlogCard
                    key={blog.id}
                    blog={blog}
                    index={index}
                    totalPosts={blogs.length}
                  />
                ))}

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <h2 className="text-2xl font-semibold text-white mb-4">No blogs found</h2>
                <p className="text-paragraph">
                  {searchTerm || selectedCategory
                    ? 'Try adjusting your search terms or category filter.'
                    : 'No blogs available at the moment.'}
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <BlogSidebar
            showSearch={true}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            selectedCategory={selectedCategory}
            onCategorySelect={handleCategorySelect}
          />
        </div>
      </section>
    </InnerPageLayout>
  );
}
